import { supabase } from '../lib/supabase';

/**
 * Base da camada de acesso a dados.
 *
 * Todo acesso ao banco passa por `src/data/`. Nenhum componente ou hook deve
 * importar `supabase` direto — é essa regra que torna possível, mais adiante,
 * ler de um armazenamento local e enfileirar as escritas sem caçar 69 pontos
 * espalhados pelo código.
 *
 * As funções daqui recebem nome de intenção (`listAnimals`, `saveFivSession`),
 * não de tabela. A fila offline vai precisar registrar *o que o usuário quis
 * fazer* para reenviar depois; `from('animals').update(...)` não carrega essa
 * informação, `updateAnimal(id, campos)` carrega.
 */

/**
 * Desembrulha a resposta do supabase-js: devolve o dado e estoura no erro.
 *
 * Os chamadores já faziam `if (error) throw error` na linha seguinte —
 * centralizar aqui só tira a repetição.
 */
export async function run(query) {
  const { data, error } = await query;
  if (error) throw error;
  return data;
}

/**
 * Como `run`, mas lê todas as páginas de uma listagem.
 *
 * O PostgREST corta toda resposta em 1000 linhas, sem erro e sem aviso: a
 * lista simplesmente acaba ali. A aba Animais ordenava por brinco e cortava
 * em 1000 de 1777 — e quase todos os touros e doadoras ficavam do lado de
 * fora, sumindo do filtro. `montar` recebe uma consulta nova a cada página
 * e deve ter ordem estável (desempate por id), senão a página seguinte pode
 * repetir ou pular linha.
 */
const TAMANHO_DA_PAGINA = 1000;

export async function runAllPages(montar) {
  const linhas = [];
  for (let de = 0; ; de += TAMANHO_DA_PAGINA) {
    const pagina = await run(montar().range(de, de + TAMANHO_DA_PAGINA - 1));
    linhas.push(...(pagina || []));
    if (!pagina || pagina.length < TAMANHO_DA_PAGINA) return linhas;
  }
}

/**
 * Igual a `run`, mas engole o erro e devolve um padrão.
 *
 * Existe para os poucos pontos que hoje ignoram a falha de propósito (leituras
 * acessórias, como buscar RGD para enfeitar um PDF). Manter esse comportamento
 * explícito evita transformar um enfeite que falhou em tela de erro.
 */
export async function runOrDefault(query, fallback = null) {
  const { data, error } = await query;
  if (error) {
    console.warn('[data] consulta acessória falhou:', error.message);
    return fallback;
  }
  return data ?? fallback;
}

export { supabase };
